import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Sparkles, ArrowRight } from 'lucide-react';

// Pre-set Q&A rule book — keep it lightweight, no LLM calls.
const QUICK_QUESTIONS = [
  {
    q: 'How does ConnectMail work?',
    a: "ConnectMail helps professionals build referral networks, create professional email signatures, advertise in each other's signatures, post in the feed, join groups, host events, and grow visibility with people they already communicate with.",
  },
  {
    q: 'Is it really free for founding members?',
    a: 'Yes. The first 1,000 members receive Founding Member status with premium access included. Founding Members also receive one free ad slot. Additional ad slots can be unlocked separately.',
  },
  {
    q: 'How do I earn from my email signature?',
    a: 'Your email signature can include advertising placements for other professionals. When advertisers purchase placements connected to your signature, you can earn from that exposure while continuing to send emails like you normally do.',
  },
  {
    q: 'Who should join ConnectMail?',
    a: 'ConnectMail is built for Realtors, Insurance Agents, Loan Officers, Attorneys, Accountants, Financial Advisors, Car Sales Professionals, Business Owners, and other professionals who want more referrals, exposure, and professional relationships.',
  },
  {
    q: 'What do I get when I sign up?',
    a: 'You get a professional profile, access to the ConnectMail network, signature builder, groups, feed, events, marketplace, AI networking coach, and Founding Member benefits if you join within the first 1,000 members.',
  },
];

// Fire-and-forget analytics ping. Silently no-ops on failure.
const trackEvent = (event, payload = {}) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/chat-event`;
    const body = JSON.stringify({ event, ...payload });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    } else {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
    }
  } catch (_) {}
};

export const LandingChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  const [messages, setMessages] = useState([
    {
      from: 'bot',
      text: "Hi! I can help you understand how ConnectMail helps professionals connect, promote each other, and earn through email signatures. What would you like to know?",
    },
  ]);
  const [askedQuestions, setAskedQuestions] = useState(new Set());
  const [showCta, setShowCta] = useState(false);
  const bodyRef = useRef(null);

  // Auto-scroll to latest message
  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, showCta]);

  const open = () => {
    setIsOpen(true);
    if (!hasOpenedOnce) {
      setHasOpenedOnce(true);
      trackEvent('chat_opened');
    }
  };

  const close = () => setIsOpen(false);

  const handleQuestion = (item) => {
    trackEvent('chat_question_clicked', { question: item.q });
    setMessages((prev) => [
      ...prev,
      { from: 'user', text: item.q },
      { from: 'bot', text: item.a },
    ]);
    const next = new Set(askedQuestions);
    next.add(item.q);
    setAskedQuestions(next);
    // Soft CTA after 2+ answered questions
    if (next.size >= 2 && !showCta) setShowCta(true);
  };

  const handleCtaClick = (where) => {
    trackEvent('chat_cta_clicked', { destination: where });
    window.location.href = where === 'signup' ? '/register' : '#how-it-works';
  };

  const remainingQuestions = QUICK_QUESTIONS.filter((q) => !askedQuestions.has(q.q));

  return (
    <>
      {/* Floating chat bubble */}
      {!isOpen && (
        <button
          onClick={open}
          aria-label="Open ConnectMail Assistant"
          data-testid="landing-chat-bubble"
          className="fixed z-40 bottom-4 right-4 sm:bottom-6 sm:right-6 group flex items-center gap-2 px-4 py-3 rounded-full shadow-xl shadow-emerald-500/30 bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-600 text-white font-semibold text-sm hover:scale-105 transition-transform"
        >
          <span className="relative flex">
            <MessageCircle className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 flex w-2.5 h-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-300 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400" />
            </span>
          </span>
          <span className="hidden sm:inline">Ask ConnectMail</span>
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          role="dialog"
          aria-label="ConnectMail Assistant"
          data-testid="landing-chat-panel"
          className="fixed z-40 inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[380px] max-h-[80vh] sm:max-h-[600px] flex flex-col rounded-2xl overflow-hidden bg-white shadow-2xl border border-emerald-100 animate-in fade-in slide-in-from-bottom-4 duration-200"
        >
          {/* Header */}
          <div className="bg-gradient-to-br from-[#1e3a5f] via-[#1f7ab3] to-emerald-600 px-4 py-3 flex items-center justify-between text-white">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-white/95 flex items-center justify-center flex-shrink-0">
                <img src="/brand/logo-icon.png" alt="" className="w-6 h-6 object-contain" />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-sm leading-tight font-['Outfit']" data-testid="chat-bot-name">ConnectMail Assistant</p>
                <p className="text-[10px] text-white/80 flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-300" />
                  Online · Ready to help
                </p>
              </div>
            </div>
            <button
              onClick={close}
              aria-label="Close chat"
              data-testid="landing-chat-close"
              className="w-7 h-7 rounded-md hover:bg-white/15 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div ref={bodyRef} className="flex-1 overflow-y-auto bg-gradient-to-b from-emerald-50/40 to-white px-3 py-3 space-y-2.5" data-testid="landing-chat-body">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-snug ${
                    m.from === 'user'
                      ? 'bg-emerald-600 text-white rounded-br-sm'
                      : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm shadow-sm'
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}

            {/* Soft signup CTA after 2+ answered questions */}
            {showCta && (
              <div className="my-2 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50 p-3" data-testid="chat-cta-card">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-amber-600" />
                  <p className="text-xs font-bold text-amber-900">Want to claim your Founding Member spot?</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleCtaClick('signup')}
                    data-testid="chat-cta-signup"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
                  >
                    Sign Up Free <ArrowRight className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleCtaClick('learn')}
                    data-testid="chat-cta-learn"
                    className="px-3 py-2 rounded-lg text-xs font-semibold text-amber-800 hover:bg-amber-100 border border-amber-300 transition-colors"
                  >
                    Learn More
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Quick question chips */}
          {remainingQuestions.length > 0 && (
            <div className="border-t border-gray-100 bg-white px-3 py-2.5 space-y-1.5" data-testid="chat-quick-questions">
              <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Quick questions</p>
              <div className="flex flex-col gap-1.5">
                {remainingQuestions.map((q) => (
                  <button
                    key={q.q}
                    onClick={() => handleQuestion(q)}
                    data-testid={`chat-q-${q.q.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 30)}`}
                    className="text-left text-[13px] font-medium text-gray-700 hover:text-emerald-700 hover:bg-emerald-50 border border-gray-200 hover:border-emerald-300 rounded-lg px-2.5 py-1.5 transition-colors"
                  >
                    {q.q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {remainingQuestions.length === 0 && !showCta && (
            <div className="border-t border-gray-100 bg-white px-3 py-3 text-center">
              <p className="text-xs text-gray-500">Thanks for chatting!</p>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-100 bg-white px-3 py-2 flex items-center justify-between text-[10px] text-gray-400">
            <span>Powered by ConnectMail</span>
            <button
              onClick={() => handleCtaClick('signup')}
              className="text-emerald-600 hover:text-emerald-700 font-semibold inline-flex items-center gap-1"
              data-testid="chat-footer-signup"
            >
              <Send className="w-3 h-3" /> Sign up
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default LandingChatBot;
